// src/utils/follow/queryConfig.js

/**
 * Centralized React Query configuration for follow-related queries
 *
 * Provides optimized caching strategies, stale times, and retry logic
 * for all follow-related operations to improve performance.
 *
 * Requirements: 6.3, 8.1, 8.2
 */

// Default cache times for different types of follow data
export const FOLLOW_CACHE_CONFIG = {
  // Follow status between two users - changes frequently
  followStatus: {
    staleTime: 3 * 60 * 1000, // 3 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  },

  // Bulk follow status - used for lists, can be cached longer
  bulkFollowStatus: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  },

  // User profiles - relatively stable data
  userProfile: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  },

  // Follower/following lists - can change but not frequently
  userLists: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    keepPreviousData: true, // Keep previous page data while loading new page
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  },

  // Current user data - changes less frequently
  currentUser: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 20 * 60 * 1000, // 20 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to app
    refetchOnMount: false,
  },
};

// Retry configuration for different error types
export const FOLLOW_RETRY_CONFIG = {
  // Standard retry logic for most operations
  standard: {
    retry: (failureCount, error) => {
      // Don't retry on auth errors or client errors (4xx)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        return false;
      }
      // Retry up to 2 times for server errors or network issues
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  },

  // More aggressive retry for critical operations like follow/unfollow
  critical: {
    retry: (failureCount, error) => {
      // Don't retry on auth errors or client errors (4xx)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        return false;
      }
      // Retry up to 3 times for critical operations
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  },

  // Conservative retry for bulk operations
  bulk: {
    retry: (failureCount, error) => {
      // Don't retry on auth errors or client errors (4xx)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        return false;
      }
      // Only retry once for bulk operations to avoid overwhelming server
      return failureCount < 1;
    },
    retryDelay: () => 2000, // Fixed 2 second delay
  },
};

// Query key factories for consistent cache keys
export const followQueryKeys = {
  // Base keys
  all: ["follow"],
  status: () => [...followQueryKeys.all, "status"],
  lists: () => [...followQueryKeys.all, "lists"],
  users: () => [...followQueryKeys.all, "users"],

  // Specific keys
  followStatus: (userId) => [...followQueryKeys.status(), userId],
  bulkFollowStatus: (userIds) => [
    ...followQueryKeys.status(),
    "bulk",
    userIds.sort(),
  ],
  followers: (userId, page, limit) => [
    ...followQueryKeys.lists(),
    "followers",
    userId,
    page,
    limit,
  ],
  following: (userId, page, limit) => [
    ...followQueryKeys.lists(),
    "following",
    userId,
    page,
    limit,
  ],
  userProfile: (userId) => [...followQueryKeys.users(), "profile", userId],
};

// Cache invalidation helpers
export const followCacheUtils = {
  // Invalidate all follow-related queries for a user
  invalidateUserFollowData: (queryClient, userId) => {
    queryClient.invalidateQueries(followQueryKeys.followStatus(userId));
    queryClient.invalidateQueries(followQueryKeys.followers(userId, 1, 20));
    queryClient.invalidateQueries(followQueryKeys.following(userId, 1, 20));
    queryClient.invalidateQueries(followQueryKeys.userProfile(userId));
  },

  // Invalidate bulk follow status queries that include a specific user
  invalidateBulkQueriesForUser: (queryClient, userId) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const [, , type, userIds] = query.queryKey;
        return (
          type === "bulk" && Array.isArray(userIds) && userIds.includes(userId)
        );
      },
    });
  },

  // Update follow counts optimistically
  updateFollowCounts: (queryClient, userId, isFollowing, increment = true) => {
    const delta = increment ? (isFollowing ? 1 : -1) : isFollowing ? -1 : 1;

    // Update target user's follower count
    queryClient.setQueryData(followQueryKeys.userProfile(userId), (old) => ({
      ...old,
      followerCount: Math.max((old?.followerCount || 0) + delta, 0),
    }));

    // Update current user's following count
    queryClient.setQueryData(["fetchUser"], (old) => ({
      ...old,
      followingCount: Math.max(
        (old?.followingCount || 0) + (isFollowing ? delta : -delta),
        0
      ),
    }));
  },

  // Prefetch related data for better UX
  prefetchUserData: async (queryClient, userId, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();

    // Prefetch user profile if not already cached
    queryClient.prefetchQuery({
      queryKey: followQueryKeys.userProfile(userId),
      queryFn: () =>
        import("../../utils/follow/followApi").then((api) =>
          api.getUserProfile(userId, token)
        ),
      ...FOLLOW_CACHE_CONFIG.userProfile,
    });

    // Prefetch follow status if not already cached
    queryClient.prefetchQuery({
      queryKey: followQueryKeys.followStatus(userId),
      queryFn: () =>
        import("../../utils/follow/followApi").then((api) =>
          api.getFollowStatus(userId, token)
        ),
      ...FOLLOW_CACHE_CONFIG.followStatus,
    });
  },
};

// Performance monitoring helpers
export const followPerformanceUtils = {
  // Track query performance
  trackQueryPerformance: (queryKey, startTime) => {
    const endTime = Date.now();
    const duration = endTime - startTime;

    // Log slow queries for monitoring
    if (duration > 2000) {
      console.warn(`Slow follow query detected:`, {
        queryKey,
        duration: `${duration}ms`,
        timestamp: new Date().toISOString(),
      });
    }

    return duration;
  },

  // Batch multiple follow status requests
  batchFollowStatusRequests: (userIds, maxBatchSize = 50) => {
    const batches = [];
    for (let i = 0; i < userIds.length; i += maxBatchSize) {
      batches.push(userIds.slice(i, i + maxBatchSize));
    }
    return batches;
  },
};
